import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { updateUserPreferences } from '../services/authService';
import { getNewFeatures, getLatestConfigVersion } from '../config/featureHighlights';

/**
 * Hook to manage feature highlight display for admin users
 * 
 * Shows new features modal when:
 * 1. User is authenticated and is an admin
 * 2. There are features in versions newer than user's last_seen_version
 * 
 * @returns {Object} { showModal, newFeatures, dismissHighlights }
 */
export const useFeatureHighlights = () => {
  const { currentUser, actualIsAdmin, updateUserData } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [newFeatures, setNewFeatures] = useState([]);
  const [isChecking, setIsChecking] = useState(false);

  // Check for new features when user logs in
  useEffect(() => {
    const checkForNewFeatures = async () => {
      // Only check for admins who are logged in
      if (!currentUser || !actualIsAdmin || isChecking) {
        return;
      }

      setIsChecking(true);

      try {
        // Get user's last seen version from preferences
        const lastSeenVersion = currentUser.preferences?.last_seen_version || null;
        
        console.log('[FeatureHighlights] Checking for new features...');
        console.log('[FeatureHighlights] Last seen version:', lastSeenVersion);

        // Get features newer than last seen version (up to 5 versions)
        const features = getNewFeatures(lastSeenVersion, 5);
        
        console.log('[FeatureHighlights] New features found:', features.length > 0 ? features : 'none');

        if (features.length > 0) {
          setNewFeatures(features);
          setShowModal(true);
        }
      } catch (error) {
        console.error('[FeatureHighlights] Error checking for new features:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkForNewFeatures();
  }, [currentUser, actualIsAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler to dismiss the modal and update user's last_seen_version
  const dismissHighlights = useCallback(async () => {
    setShowModal(false);
    
    if (!currentUser) return;

    try {
      // Get the latest version from the config
      const latestVersion = getLatestConfigVersion();
      
      if (latestVersion) {
        console.log('[FeatureHighlights] Updating last_seen_version to:', latestVersion);
        
        // Update preferences on the server
        await updateUserPreferences({ last_seen_version: latestVersion });
        
        // Update local user state
        const updatedPreferences = {
          ...(currentUser.preferences || {}),
          last_seen_version: latestVersion
        };
        
        updateUserData({
          ...currentUser,
          preferences: updatedPreferences
        });
        
        console.log('[FeatureHighlights] Preferences updated successfully');
      }
    } catch (error) {
      console.error('[FeatureHighlights] Error updating last_seen_version:', error);
      // Don't show error to user - this is a non-critical feature
    }
  }, [currentUser, updateUserData]);

  return {
    showModal,
    newFeatures,
    dismissHighlights
  };
};

export default useFeatureHighlights;
